import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Camera from "@insite/mobius/Icons/Camera";
import FileText from "@insite/mobius/Icons/FileText";
import Rss from "@insite/mobius/Icons/Rss";
import User from "@insite/mobius/Icons/User";
import Video from "@insite/mobius/Icons/Video";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";
/* Custom Code - Start */
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { ExtendedCssProps } from "@store/Framework/Types/StyledTheme";
import Facebook from "@store/Library/Components/Icons/Brand/Facebook";
import Instagram from "@store/Library/Components/Icons/Brand/Instagram";
import Linkedin from "@store/Library/Components/Icons/Brand/Linkedin";
import Twitter from "@store/Library/Components/Icons/Brand/Twitter";
import XTwitter from "@store/Library/Components/Icons/Brand/Twitter";
import Youtube from "@store/Library/Components/Icons/Brand/Youtube";
import { widgetWrapperStyles } from "@store/Library/Components/Toolkit/WidgetWrapper";
import { focusStyles } from "@store/Themes/common/mixins/common";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
/* Custom Code - End */

/* Store Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
/* Store Custom Code - End */

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    visibilityState = "visibilityState",
    iconSize = "iconSize",
    // Removed - iconColor
    // Removed - backgroundColor
    // Removed - linksPerRow
    customCSS = "customCSS",
    // Custom Code
    label = "label",
    iconSpacing = "iconSpacing",
}

const enum icons {
    instagram = "Instagram",
    facebook = "Facebook",
    youtube = "Youtube",
    twitter = "Twitter",
    xTwitter = "X",
    rss = "Rss",
    user = "User",
    linkedin = "Linkedin",
    camera = "Camera",
    video = "Video",
    notebook = "Notebook",
    // Custom Code
    pinterest = "Pinterest",
}

const enum visibilityState {
    both = "both",
    label = "label",
    icon = "icon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "right";
        [fields.visibilityState]: visibilityState;
        [fields.title]: string;
        [fields.iconSize]: number;
        // Removed - linksPerRow
        // Removed - iconColor
        // Removed - backgroundColor
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
        // Custom Code
        [fields.label]: string;
        [fields.iconSpacing]: string;
    };
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

export interface LinkListStyles {
    socialLinkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // Custom Code
    wrapper?: InjectableCss;
    label?: TypographyPresentationProps;
}

export const linkListStyles: LinkListStyles = {
    socialLinkListWrapper: {
        css: css`
            // width: 100%;
            // padding: 10px 0;
            display: flex;
            flex-wrap: wrap;
            // align-items: flex-start;
        `,
    },
    linkWrapper: {
        css: css`
            display: flex;
            /* Store Custom Code - Start */
            height: ${pxToRem(23)};
            margin: 0 ${pxToRem(10)};
            -ms-transform: skewX(-20deg);
            -webkit-transform: skewX(-20deg);
            transform: skewX(-20deg);
            background-color: ${getColor("secondary")};
            /* Store Custom Code - End */
        `,
    },
    link: {
        typographyProps: {
            color: "inherit",
        },
        icon: {
            iconProps: {
                color: "inherit",
                /* Store Custom Code - Start */
                css: css`
                    width: ${pxToRem(16)};
                    height: ${pxToRem(16)};
                `,
                /* Store Custom Code - End */
            },
        },
        css: css`
            ${focusStyles()}
            color: inherit;
            transition: ${transitionHoverStyles("color", "transform")};
            /* Store Custom Code - Start */
            padding: 0 ${pxToRem(4)};
            -ms-transform: skewX(20deg);
            -webkit-transform: skewX(20deg);
            transform: skewX(20deg);
            display: inline-flex;
            align-items: center;

            &:hover,
            &:active {
                ${(x: ExtendedCssProps) => (x.isIconOnly ? "transform: scale(1.2) skewX(20deg);" : null)}
                text-decoration: underline;
            }
            /* Store Custom Code - End */
        `,
    },
    wrapper: {
        css: css`
            ${widgetWrapperStyles.css}
            display: flex;
            align-items: center;
            justify-content: center;

            ${breakpoint("md")} {
                justify-content: left;
            }
        `,
    },
    label: {
        forwardAs: "p",
    },
};

export const socialLinksStyles = linkListStyles;

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.youtube:
            return Youtube;
        case icons.twitter:
            return Twitter;
        case icons.xTwitter:
            return XTwitter;
        case icons.facebook:
            return Facebook;
        case icons.instagram:
            return Instagram;
        case icons.linkedin:
            return Linkedin;
        case icons.notebook:
            return FileText;
        case icons.rss:
            return Rss;
        case icons.user:
            return User;
        case icons.video:
            return Video;
        case icons.camera:
            return Camera;
        default:
            break;
    }

    return "";
};

export const SocialLinks: FC<OwnProps> = ({ fields }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);

    const styles = useMergeStyles("socialLinks", linkListStyles);

    // if (hasMissingCategories) {
    //     return null;
    // }
    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css /* Custom Code */}
            ${fields.customCSS}
        `,
    };
    const socialLinkListStyle = css`
        ${fields.direction === "horizontal" ? "flex-direction: row;" : "flex-direction: column;"}
        /* Removed - backgroundColor */
        margin: -${pxToRem(fields.iconSpacing)}; /* Custom Code */
        ${styles.socialLinkListWrapper?.css}
    `;

    const linkWrapperStyle = css`
        /* Removed - flex-basis */
        /* Removed - flex-grow */
        margin: ${pxToRem(fields.iconSpacing)}; /* Custom Code */
        ${styles.linkWrapper?.css}
    `;
        /* Custom Code - Start */
    const iconStyle = css`
        ${() => {
            if (fields.visibilityState === visibilityState.both) {
                return `margin-${fields.alignment === "right" ? "left" : "right"}: ${pxToRem(4)};`;
            }

            return "margin: 0;";
        }}
        ${styles.link?.icon?.iconProps?.css}
    `;

    const labelStyle = css`
        margin-right: ${pxToRem(fields.iconSpacing)};
        ${styles.label?.css}
    `;
    /* Custom Code - End */

    const showIcon = fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.icon;
    const showLabel =
        fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.label;

    return (
        <StyledWrapper {...customCssWrapper}>
            {/* Custom Code - Start */}
            {fields.label ? (
                <Typography className="social-label" css={labelStyle}>
                    {fields.label}
                </Typography>
            ) : null}
            {/* Custom Code - End */}
            <StyledWrapper className="social-links" css={socialLinkListStyle}>
                {links.map((link, index) => (
                    <StyledWrapper
                        className="link-wrapper"
                        css={linkWrapperStyle}
                         
                        key={index}
                    >
                        {link.url && (
                            <Link
                                className="social-link"
                                {...styles.link}
                                href={link.url}
                                target={
                                    fields.links[index].fields.openInNewWindow ? "_blank" : "_self" /* Custom Code */
                                }
                                icon={{
                                    iconProps: {
                                        ...styles.link?.icon, // Custom Code
                                        size: fields.iconSize,
                                        // Removed - iconColor
                                        css: iconStyle, // Custom Code
                                        src: showIcon ? GetIcon(fields.links[index].fields.icon) : undefined,
                                        title: fields.links[index].fields.title,
                                    },
                                    position: fields.alignment,
                                }}
                                isIconOnly={!showLabel} // Custom Code
                            >
                                {showLabel && fields.links[index].fields.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

/* Custom Code - Start */
const defaultCustomCss = `.social-links{
}

.link-wrapper{
}

.social-link{
}

.social-label{
}`;
/* Custom Code - End */

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "thumbs-up",
    isSystem: true,
    fieldDefinitions: [
        /* Custom Code - Start */
        {
            name: fields.label,
            editorTemplate: "TextField",
            tab: contentTab,
            defaultValue: "Follow us:",
            fieldType: "Translatable",
        },
        {
            name: fields.iconSpacing,
            editorTemplate: "IntegerField",
            tab: contentTab,
            defaultValue: 4,
            fieldType: "General",
            isRequired: true,
        },
        /* Custom Code - End */
        {
            name: fields.links,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Instagram", value: icons.instagram },
                        { displayName: "Youtube", value: icons.youtube },
                        { displayName: "Facebook", value: icons.facebook },
                        { displayName: "X (Twitter)" /* Custom Code */, value: icons.twitter },
                        // Removed - xTwitter
                        { displayName: "Rss", value: icons.rss },
                        { displayName: "User", value: icons.user },
                        { displayName: "Linkedin", value: icons.linkedin },
                        { displayName: "Camera", value: icons.camera },
                        { displayName: "Video", value: icons.video },
                        { displayName: "Notebook", value: icons.notebook },
                        { displayName: "Pinterest", value: icons.pinterest }, // Custom Code
                    ],
                    defaultValue: "",
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: true, // Custom Code
                },
            ],
        },
        {
            name: fields.direction,
            displayName: "Display",
            editorTemplate: "RadioButtonsField",
            defaultValue: "horizontal",
            horizontal: true,
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 1,
            options: [
                {
                    displayName: "Column",
                    value: "vertical",
                },
                {
                    displayName: "Row",
                    value: "horizontal",
                },
            ],
        },
        {
            name: fields.alignment,
            displayName: "Text Position",
            editorTemplate: "RadioButtonsField",
            defaultValue: "left",
            horizontal: true,
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 2,
            options: [
                {
                    displayName: "Right of icon", // Custom Code
                    value: "left",
                },
                {
                    displayName: "Left of icon", // Custom Code
                    value: "right",
                },
            ],
        },
        {
            name: fields.visibilityState,
            displayName: "Visibility",
            editorTemplate: "RadioButtonsField",
            tab: settingsTab,
            options: [
                {
                    displayName: "Show Both",
                    value: visibilityState.both,
                },
                {
                    displayName: "Show Label Only",
                    value: visibilityState.label,
                },
                {
                    displayName: "Show Icon Only",
                    value: visibilityState.icon,
                },
            ],
            defaultValue: visibilityState.icon, // Custom Code
            fieldType: "General",
            sortOrder: 4,
        },
        {
            name: fields.iconSize,
            displayName: "Icon Size",
            editorTemplate: "IntegerField",
            defaultValue: 20, // Custom Code
            min: 1,
            fieldType: "General",
            sortOrder: 3,
            tab: settingsTab,
        },
        {
            name: fields.customCSS,
            displayName: "Custom CSS",
            editorTemplate: "CodeField",
            fieldType: "General",
            tab: advancedTab,
            defaultValue: defaultCustomCss,
            isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            validate: value => {
                if (value === undefined || value === null) {
                    return "";
                }

                const result = cssLinter.parse(value, { silent: true });
                if (result?.stylesheet?.parsingErrors) {
                    // the error output at this time only has room for one line so we just show the first error
                    return result.stylesheet.parsingErrors.length <= 0
                        ? ""
                        : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                }

                return "Unable to parse Css";
            },
            options: {
                mode: "css",
                lint: true,
                autoRefresh: true,
            },
        },
    ],
};

const widgetModule: WidgetModule = {
    component: SocialLinks,
    definition,
};

export default widgetModule;
